/**
 * useEnergy Hook
 *
 * Provides energy consumption data, costs, and sync functionality
 * from the energy-sync Edge Function.
 *
 * @example
 * const {
 *   meters, selectedMeterId, setSelectedMeterId,
 *   daily, monthly, isLoading, error,
 *   isSyncing, syncEnergy
 * } = useEnergy()
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

const FUNCTION_NAME = 'energy-sync'

/**
 * Call the energy-sync Edge Function
 */
async function callEnergyFunction(path, options = {}) {
  const { method = 'GET', body, params } = options

  let fullPath = `${FUNCTION_NAME}/${path}`
  if (params) {
    fullPath += `?${params.toString()}`
  }

  const invokeOptions = { method }
  if (body) {
    invokeOptions.body = body
  }

  const { data, error } = await supabase.functions.invoke(fullPath, invokeOptions)

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  if (data?.error) {
    const err = new Error(data.message || data.error)
    err.retryable = data.retryable ?? false
    throw err
  }

  return data
}

/**
 * Hook to manage energy data and sync
 */
export function useEnergy() {
  // Meters state
  const [meters, setMeters] = useState([])
  const [selectedMeterId, setSelectedMeterId] = useState(null)

  // Data state
  const [daily, setDaily] = useState([])
  const [monthly, setMonthly] = useState([])

  // Loading state
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [error, setError] = useState(null)

  // =========================================================================
  // Fetch meters
  // =========================================================================

  const fetchMeters = useCallback(async () => {
    try {
      const response = await callEnergyFunction('meters')
      const meterData = response.data || []
      setMeters(meterData)

      // Auto-select first meter if none selected
      if (!selectedMeterId && meterData.length > 0) {
        setSelectedMeterId(meterData[0].id)
      }

      return meterData
    } catch (err) {
      console.error('[useEnergy] Failed to fetch meters:', err)
      setError(err.message)
      return []
    }
  }, [selectedMeterId])

  // =========================================================================
  // Fetch data
  // =========================================================================

  const fetchData = useCallback(async (meterId) => {
    if (!meterId) return

    setIsLoading(true)
    setError(null)

    try {
      // Fetch daily and monthly in parallel
      const [dailyResponse, monthlyResponse] = await Promise.all([
        callEnergyFunction('daily', {
          params: new URLSearchParams({ meter_id: meterId })
        }),
        callEnergyFunction('monthly', {
          params: new URLSearchParams({ meter_id: meterId, months: '24' })
        })
      ])

      setDaily(dailyResponse.data || [])
      setMonthly(monthlyResponse.data || [])
    } catch (err) {
      console.error('[useEnergy] Failed to fetch data:', err)
      setError(err.message)
      setDaily([])
      setMonthly([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // =========================================================================
  // Sync from Octopus API
  // =========================================================================

  const syncEnergy = useCallback(async () => {
    setIsSyncing(true)
    setError(null)

    try {
      const response = await callEnergyFunction('sync', {
        method: 'POST',
        body: selectedMeterId ? { meter_id: selectedMeterId } : {}
      })

      // Refresh data after sync
      if (selectedMeterId) {
        await fetchData(selectedMeterId)
      }

      return response.data
    } catch (err) {
      console.error('[useEnergy] Sync failed:', err)
      setError(err.message)
      throw err
    } finally {
      setIsSyncing(false)
    }
  }, [selectedMeterId, fetchData])

  // =========================================================================
  // Effects
  // =========================================================================

  // Load meters on mount
  useEffect(() => {
    fetchMeters()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load data when meter changes
  useEffect(() => {
    if (selectedMeterId) {
      fetchData(selectedMeterId)
    }
  }, [selectedMeterId, fetchData])

  // =========================================================================
  // Return
  // =========================================================================

  return {
    meters,
    selectedMeterId,
    setSelectedMeterId,
    daily,
    monthly,
    isLoading,
    isSyncing,
    error,
    syncEnergy,
    refresh: () => selectedMeterId && fetchData(selectedMeterId)
  }
}

export default useEnergy
